import type { DesignPerson, DesignScopeRoot } from "./designUtils";

/**
 * Design fields as held by the create/edit forms. Every field is optional
 * because the create modal submits a four-field subset while the edit page
 * submits the whole form.
 */
export type DesignMutationFormInput = {
  baselineWorkspaceId?: string | null;
  designWorkspaceId?: string | null;
  name?: string | null;
  designType?: string | null;
  sponsors?: DesignPerson[] | null;
  leads?: DesignPerson[] | null;
  participants?: DesignPerson[] | null;
  scopeRoots?: DesignScopeRoot[] | null;
  scopeRootGroupIds?: string[] | null;
  primaryDriver?: string | null;
  purpose?: string | null;
  successCriteria?: string | null;
  benefits?: string | null;
  workflowStage?: string | null;
  startDate?: string | null;
  targetImplementationDate?: string | null;
  baselineDataDate?: string | null;
};

/** Matches the API `DesignInput` accepted by `createDesign` and `updateDesign`. */
export type DesignMutationInput = {
  baselineWorkspaceId?: string | null | undefined;
  designWorkspaceId: string | null;
  name?: string | null | undefined;
  designType?: string | null | undefined;
  sponsorPersonIds?: string[] | undefined;
  leadPersonIds?: string[] | undefined;
  participantPersonIds?: string[] | undefined;
  scopeRootGroupIds?: string[] | null | undefined;
  primaryDriver?: string | null | undefined;
  purpose?: string | null | undefined;
  successCriteria?: string | null | undefined;
  benefits?: string | null | undefined;
  workflowStage?: string | null | undefined;
  startDate: string | null;
  targetImplementationDate: string | null;
  baselineDataDate: string | null;
};

export const personIdsForDesignInput = (
  people?: (DesignPerson | null | undefined)[] | null
): string[] =>
  (people || [])
    .map((p) => p?.aggregateId)
    .filter((aggregateId): aggregateId is string => Boolean(aggregateId));

const optionalDate = (dateValue?: string | null): string | null =>
  dateValue || null;

const samePeople = (
  next?: (DesignPerson | null | undefined)[] | null,
  loaded?: (DesignPerson | null | undefined)[] | null
): boolean => {
  const a = personIdsForDesignInput(next);
  const b = personIdsForDesignInput(loaded);
  return a.length === b.length && a.every((id, index) => id === b[index]);
};

type OmitUnchangedDesignPeopleOptions = {
  canEditPeople?: boolean;
};

const shouldOmitPeopleField = (
  next: DesignPerson[] | null | undefined,
  loaded: DesignPerson[] | null | undefined,
  { canEditPeople = true }: OmitUnchangedDesignPeopleOptions
): boolean => !canEditPeople || samePeople(next, loaded);

/**
 * Drop people arrays that are unchanged from the loaded design.
 *
 * The API treats the mere presence of any people field as a people update
 * (`hasDesignPeopleUpdate`) and then enforces the people-write bar, which is
 * WAC on the *tenant default* workspace - a different, stricter bar than design
 * edit access (WAC on the design's own workspace). The edit form always holds
 * populated people arrays, so re-submitting them unchanged 403s any caller who
 * only edited structure fields. Omitting them leaves the stored people
 * untouched: `designDatabase.updateDesign` only writes the `people` column when
 * one of the three role arrays is present.
 */
export const omitUnchangedDesignPeople = <T extends DesignMutationFormInput>(
  input: T,
  loaded?: DesignMutationFormInput | null,
  options: OmitUnchangedDesignPeopleOptions = {}
): T => ({
  ...input,
  sponsors: shouldOmitPeopleField(input.sponsors, loaded?.sponsors, options)
    ? undefined
    : input.sponsors,
  leads: shouldOmitPeopleField(input.leads, loaded?.leads, options)
    ? undefined
    : input.leads,
  participants: shouldOmitPeopleField(
    input.participants,
    loaded?.participants,
    options
  )
    ? undefined
    : input.participants,
});

export const buildDesignMutationInput = (
  input: DesignMutationFormInput
): DesignMutationInput => ({
  baselineWorkspaceId: input.baselineWorkspaceId,
  designWorkspaceId: input.designWorkspaceId || null,
  name: input.name,
  designType: input.designType,
  sponsorPersonIds: input.sponsors
    ? personIdsForDesignInput(input.sponsors)
    : undefined,
  leadPersonIds: input.leads ? personIdsForDesignInput(input.leads) : undefined,
  participantPersonIds: input.participants
    ? personIdsForDesignInput(input.participants)
    : undefined,
  scopeRootGroupIds: input.scopeRoots
    ? input.scopeRoots.map((r) => r.groupId)
    : input.scopeRootGroupIds,
  primaryDriver: input.primaryDriver,
  purpose: input.purpose,
  successCriteria: input.successCriteria,
  benefits: input.benefits,
  workflowStage: input.workflowStage,
  startDate: optionalDate(input.startDate),
  targetImplementationDate: optionalDate(input.targetImplementationDate),
  baselineDataDate: optionalDate(input.baselineDataDate),
});
